import React, { useState } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import type { KindFilter } from '../../hooks/useMapsExplorer';
import type { WorldData } from '../../types/world';
type Props = {
  w: WorldData;
  mapFilter: string;
  kind: KindFilter;
  lvMin: string;
  lvMax: string;
  charLv: string;
  activeFilters: number;
  set: (key: string, value: string) => void;
  clear: () => void;
};
const kinds: {
  id: KindFilter;
  label: string;
}[] = [{
  id: 'todos',
  label: 'Todos'
}, {
  id: 'spot',
  label: 'Spots'
}, {
  id: 'invasion',
  label: 'Invasiones'
}, {
  id: 'evento',
  label: 'Eventos'
}];
const inputCls = 'h-11 w-full rounded-xl border border-white/15 bg-[#050b14] px-3 text-[15px] text-white placeholder:text-gray-600 focus:border-[#D4AF37]/70 focus:outline-none';
const labelCls = 'mb-1.5 block text-xs font-semibold text-gray-400';
export function ExplorerFilters({
  w,
  mapFilter,
  kind,
  lvMin,
  lvMax,
  charLv,
  activeFilters,
  set,
  clear
}: Props) {
  const [open, setOpen] = useState(false);
  const maps = Array.from(w.maps.values()).sort((a, b) => a.name.localeCompare(b.name));
  return <div data-id="element-1261" data-mp="3def670h4ybgl">
      <div className="flex items-center gap-2 lg:hidden" data-id="element-1262" data-mp="3deoxqshtwqoe">
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls="explorer-filters" className="inline-flex h-11 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold text-gray-200" data-id="element-1263" data-mp="3dhb4ipwwb25k">
          <SlidersHorizontal className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1264" data-mp="2tqxyp67niisl" />
          Filtros{activeFilters ? ` (${activeFilters})` : ''}
        </button>
        {activeFilters > 0 && <button type="button" onClick={clear} className="h-11 px-3 text-sm text-gray-400 hover:text-white" data-id="element-1265" data-mp="2kq9j472m0adl">Quitar filtros</button>}
      </div>

      <div id="explorer-filters" className={`${open ? 'grid' : 'hidden'} mt-3 gap-4 rounded-2xl border border-white/10 bg-[#0A1628] p-4 sm:grid-cols-2 lg:mt-0 lg:grid lg:grid-cols-[1.2fr_1.6fr_1fr_1fr]`} data-id="element-1266" data-mp="nwokjh219r59">
        <div data-id="element-1267" data-mp="121ylyx5hgays">
          <label htmlFor="f-map" className={labelCls} data-id="element-1268" data-mp="121f1b8og78h2">Mapa</label>
          <select id="f-map" value={mapFilter} onChange={e => set('mapa', e.target.value)} className={inputCls} data-id="element-1269" data-mp="16v9rmuyk6lp4">
            <option value="" data-id="element-1270" data-mp="t4ej5jrz26m2">Todos los mapas</option>
            {maps.map(m => <option key={m.id} value={m.id} data-id="element-1271" data-mp="y14yg4vycy6f">{m.name}</option>)}
          </select>
        </div>

        <fieldset data-id="element-1272" data-mp="1lr8zc4hrajtq">
          <legend className={labelCls} data-id="element-1273" data-mp="1lstvfyjpyatp">Tipo de presencia</legend>
          <div className="flex flex-wrap gap-1.5" data-id="element-1274" data-mp="1psub1mik0s2c">
            {kinds.map(k => <button key={k.id} type="button" aria-pressed={kind === k.id} onClick={() => set('tipo', k.id === 'todos' ? '' : k.id)} className={`h-11 rounded-xl px-3 text-sm font-semibold transition-colors duration-150 ${kind === k.id ? 'bg-[#D4AF37] text-[#050b14]' : 'border border-white/15 text-gray-300 hover:text-white'}`} data-id="element-1275" data-mp="1btbbhi3t6p1n">
                {k.label}
              </button>)}
          </div>
        </fieldset>

        <fieldset data-id="element-1276" data-mp="3p0khum8aa7fi">
          <legend className={labelCls} data-id="element-1277" data-mp="3oyznawlyyohn">Nivel del monstruo</legend>
          <div className="flex items-center gap-2" data-id="element-1278" data-mp="6u61kgmjlldy">
            <input aria-label="Nivel del monstruo mínimo" inputMode="numeric" placeholder="Mín." value={lvMin} onChange={e => set('nmin', e.target.value.replace(/\D/g, ''))} className={inputCls} data-id="element-1279" data-mp="6tmvpya62nga" />
            <span className="text-gray-600" data-id="element-1280" data-mp="3abehuw1mgd6k">–</span>
            <input aria-label="Nivel del monstruo máximo" inputMode="numeric" placeholder="Máx." value={lvMax} onChange={e => set('nmax', e.target.value.replace(/\D/g, ''))} className={inputCls} data-id="element-1281" data-mp="36a30tewtd09z" />
          </div>
        </fieldset>

        <div data-id="element-1282" data-mp="3f9g23kasaijm">
          <label htmlFor="f-pj" className={labelCls} data-id="element-1283" data-mp="3f8wscqvaf0go">Nivel de tu personaje</label>
          <div className="relative" data-id="element-1284" data-mp="2rjc721bnvr48">
            <input id="f-pj" inputMode="numeric" placeholder="Ej. 120" value={charLv} onChange={e => set('pj', e.target.value.replace(/\D/g, ''))} className={inputCls} aria-describedby="f-pj-help" data-id="element-1285" data-mp="2rian17co3uad" />
            {charLv && <button type="button" onClick={() => set('pj', '')} aria-label="Quitar nivel de personaje" className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center text-gray-500 hover:text-white" data-id="element-1286" data-mp="356uj6bike06l">
                <X className="h-4 w-4" data-id="element-1287" data-mp="3exyt2wtduuuk" />
              </button>}
          </div>
          <p id="f-pj-help" className="mt-1.5 text-xs text-gray-500" data-id="element-1288" data-mp="3ixz9gn01lg49">Filtra por requisito de acceso documentado.</p>
        </div>

        {activeFilters > 0 && <div className="hidden lg:col-span-4 lg:block" data-id="element-1289" data-mp="3sx81397fqing">
            <button type="button" onClick={clear} className="text-sm text-gray-400 hover:text-white" data-id="element-1290" data-mp="3sxq36mgs3r3a">Quitar filtros</button>
          </div>}
      </div>
    </div>;
}